// ApplicationPublicPage — public application form at /application/:token. NO auth.
//
// Renders dynamically from the template snapshot (sections + custom fields),
// incl. spouse + dependents repeaters. SSN is REQUIRED for everyone. Before
// submitting, every non-optional field must be filled — missing ones are
// listed in a red notice and their inputs are outlined in red.
import React, { useEffect, useState } from "react";
import { Loader2, Check, ShieldCheck, AlertCircle, CheckCircle2, Plus, Trash2 } from "lucide-react";
import { getPublicApplication, submitPublicApplication } from "../api.js";
import {
  CAMPOS_CONDUCTOR_TITULAR, CAMPOS_CON_LA_DIRECCION, CAMPOS_CARRO, CAMPOS_CONDUCTOR, CAMPOS_DIRECCION_ANTERIOR,
  CAMPOS_SEGURO_ACTUAL, CAMPOS_POLIZA_AUTO, CAMPOS_PAGO,
  filaVacia, filasQueSeEnvian, etiqueta, viviendaVaConLaDireccion,
} from "../formularioDeAuto.js";
import { nombreDelEspejo } from '../applicationMirror.js';

const MARITAL = (L) => [
  ["", L ? "Select…" : "Selecciona…"],
  ["single", L ? "Single" : "Soltero/a"],
  ["married", L ? "Married" : "Casado/a"],
  ["divorced", L ? "Divorced" : "Divorciado/a"],
  ["widowed", L ? "Widowed" : "Viudo/a"],
  ["domestic_partner", L ? "Domestic partner" : "Unión libre"],
];
const IMMIGRATION = (L) => [
  ["", L ? "Select…" : "Selecciona…"],
  ["us_citizen", L ? "U.S. citizen" : "Ciudadano/a EE.UU."],
  ["permanent_resident", L ? "Permanent resident" : "Residente permanente"],
  ["cuban_haitian_entrant", L ? "Cuban/Haitian entrant (parole)" : "Entrante cubano/haitiano (parole)"],
  ["visa_holder", L ? "Visa holder" : "Visa"],
  ["daca", "DACA"],
  ["asylum", L ? "Asylum / TPS" : "Asilo / TPS"],
  ["undocumented", L ? "Undocumented" : "Sin estatus"],
  ["other", L ? "Other" : "Otro"],
];

export function ApplicationPublicPage({ token }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [linkGone, setLinkGone] = useState(false); // 410: existió y ya no vale
  const [form, setForm] = useState(() => emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [missing, setMissing] = useState([]); // [{key,label}]
  const [done, setDone] = useState(null);

  const EN = (data?.language || "es") === "en";
  const t = (es, en) => (EN ? en : es);
  const errSet = new Set(missing.map((m) => m.key));

  useEffect(() => {
    let off = false;
    setLoadError("");
    getPublicApplication(token)
      .then((d) => {
        if (off) return;
        setData(d);
        const parts = String(d.recipientName || "").trim().split(/\s+/);
        setForm((f) => ({ ...f, firstName: parts.shift() || "", lastName: parts.join(" ") || "", email: d.recipientEmail || "" }));
      })
      .catch((err) => { if (off) return; setLinkGone(!!err.linkGone); setLoadError(err.message || String(err)); });
    return () => { off = true; };
  }, [token]);

  const sections = data?.sections || {};
  const customFields = data?.customFields || [];

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const setSpouse = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, spouse: { ...f.spouse, [k]: v } }));
  };
  const setCustom = (id) => (val) => setForm((f) => ({ ...f, customValues: { ...f.customValues, [id]: val } }));

  // ── EL FORMULARIO DE AUTO (punto 69) ────────────────────────────────────
  // Un solo juego de manejadores para los siete bloques: los campos y sus
  // desplegables salen del catalogo (crm-app/formularioDeAuto.js), asi que
  // aqui no hay una linea por pregunta — que es como se olvida una al copiar.
  const setAuto = (bloque, clave) => (e) => setForm((f) => ({
    ...f, auto: { ...f.auto, [bloque]: { ...f.auto[bloque], [clave]: e.target.value } },
  }));
  const setFila = (bloque, i, clave) => (e) => setForm((f) => {
    const filas = f.auto[bloque].slice();
    filas[i] = { ...filas[i], [clave]: e.target.value };
    return { ...f, auto: { ...f.auto, [bloque]: filas } };
  });
  const addFila = (bloque, campos) => () => setForm((f) => ({
    ...f, auto: { ...f.auto, [bloque]: [...f.auto[bloque], filaVacia(campos)] },
  }));
  const delFila = (bloque, i) => () => setForm((f) => ({
    ...f, auto: { ...f.auto, [bloque]: f.auto[bloque].filter((_, j) => j !== i) },
  }));

  // Los tres componentes del formulario de auto (CampoAuto, BloqueAuto y
  // Repetidor) viven en el ambito del MODULO, no aqui dentro. Estan justo
  // encima de `Section`, que esta abajo por exactamente el mismo motivo.

  // Aplicación espejo (punto 9): cuando la plantilla lo pide, al cónyuge y a
  // cada dependiente se les hacen LAS MISMAS preguntas que al titular — las
  // notas de salud y las preguntas propias de la plantilla.
  //
  // Las respuestas viajan en el mismo mapa `customValues`, con el nombre de la
  // persona por delante ("spouse.cf_3", "dep.0.cf_3"). Es un Record<string,
  // string> en el servidor, así que no hace falta tocar el esquema — el mismo
  // truco que ya usaba `spouse_address`.
  const espejo = !!sections.mirror;
  const clave = (quien, id) => `${quien}.${id}`;
  const HEALTH_KEY = 'healthNotes';
  const addDep = () => setForm((f) => ({ ...f, dependents: [...f.dependents, emptyDep()] }));
  const setDep = (i, k) => (e) => setForm((f) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const deps = f.dependents.slice(); deps[i] = { ...deps[i], [k]: v }; return { ...f, dependents: deps };
  });
  const delDep = (i) => setForm((f) => {
    const deps = f.dependents.filter((_, j) => j !== i);
    // Las respuestas del espejo van indexadas (dep.0.*, dep.1.*). Al quitar a
    // alguien del medio hay que CORRER las de abajo: si no, las respuestas del
    // dependiente 2 se quedan escritas como del 2 mientras él pasa a ser el 1,
    // y acaban pegadas a otra persona o desaparecidas.
    const cv = {};
    Object.entries(f.customValues || {}).forEach(([k, v]) => {
      const m = /^dep\.(\d+)\.(.+)$/.exec(k);
      if (!m) { cv[k] = v; return; }
      const idx = Number(m[1]);
      if (idx === i) return;                       // se va con la persona
      cv[idx > i ? `dep.${idx - 1}.${m[2]}` : k] = v;
    });
    return { ...f, dependents: deps, customValues: cv };
  });

  function validate() {
    const errs = [];
    const need = (cond, key, label) => { if (cond) errs.push({ key, label }); };
    const blank = (v) => !String(v || "").trim();

    // Contact (always)
    need(blank(form.firstName), "firstName", t("Nombre", "First name"));
    need(blank(form.lastName), "lastName", t("Apellido", "Last name"));
    need(blank(form.email), "email", t("Correo", "Email"));
    need(blank(form.phone), "phone", t("Teléfono", "Phone"));
    if (sections.dob) need(blank(form.dateOfBirth), "dateOfBirth", t("Fecha de nacimiento", "Date of birth"));
    if (sections.ssn) need(blank(form.ssn), "ssn", "SSN/ITIN");
    if (sections.address) {
      need(blank(form.address), "address", t("Dirección", "Address"));
      need(blank(form.city), "city", t("Ciudad", "City"));
      need(blank(form.state), "state", t("Estado", "State"));
      need(blank(form.zipCode), "zipCode", t("Código postal", "ZIP"));
    }
    if (sections.employment) {
      need(blank(form.maritalStatus), "maritalStatus", t("Estado civil", "Marital status"));
      need(blank(form.immigrationStatus), "immigrationStatus", t("Estatus migratorio", "Immigration status"));
      need(blank(form.employer), "employer", t("Empleador", "Employer"));
      need(blank(form.annualIncome), "annualIncome", t("Ingreso anual", "Annual income"));
    }
    // Spouse — all-or-nothing: only required once they start filling it.
    if (sections.spouse && spouseStarted(form.spouse)) {
      const p = form.spouse, pre = "spouse.";
      need(blank(p.firstName), pre + "firstName", t("Cónyuge: nombre", "Spouse: first name"));
      need(blank(p.lastName), pre + "lastName", t("Cónyuge: apellido", "Spouse: last name"));
      need(blank(p.email), pre + "email", t("Cónyuge: correo", "Spouse: email"));
      need(blank(p.phone), pre + "phone", t("Cónyuge: teléfono", "Spouse: phone"));
      if (sections.dob) need(blank(p.dateOfBirth), pre + "dateOfBirth", t("Cónyuge: fecha de nacimiento", "Spouse: date of birth"));
      if (sections.ssn) need(blank(p.ssn), pre + "ssn", t("Cónyuge: SSN", "Spouse: SSN"));
      need(blank(p.employer), pre + "employer", t("Cónyuge: empleador", "Spouse: employer"));
      need(blank(p.annualIncome), pre + "annualIncome", t("Cónyuge: ingreso", "Spouse: income"));
      need(blank(p.immigrationStatus), pre + "immigrationStatus", t("Cónyuge: estatus migratorio", "Spouse: immigration status"));
      need(!p.sameAddress && blank(p.address), pre + "address", t("Cónyuge: dirección", "Spouse: address"));
    }
    // Dependents — each started row is fully required.
    if (sections.dependents) {
      form.dependents.forEach((d, i) => {
        if (!depStarted(d)) return;
        const pre = `dep.${i}.`;
        need(blank(d.firstName), pre + "firstName", t(`Dependiente ${i + 1}: nombre`, `Dependent ${i + 1}: first name`));
        need(blank(d.lastName), pre + "lastName", t(`Dependiente ${i + 1}: apellido`, `Dependent ${i + 1}: last name`));
        need(blank(d.relation), pre + "relation", t(`Dependiente ${i + 1}: parentesco`, `Dependent ${i + 1}: relationship`));
        need(blank(d.email), pre + "email", t(`Dependiente ${i + 1}: correo`, `Dependent ${i + 1}: email`));
        need(blank(d.phone), pre + "phone", t(`Dependiente ${i + 1}: teléfono`, `Dependent ${i + 1}: phone`));
        if (sections.dob) need(blank(d.dateOfBirth), pre + "dateOfBirth", t(`Dependiente ${i + 1}: fecha de nacimiento`, `Dependent ${i + 1}: date of birth`));
        if (sections.ssn) need(blank(d.ssn), pre + "ssn", t(`Dependiente ${i + 1}: SSN`, `Dependent ${i + 1}: SSN`));
        need(blank(d.employer), pre + "employer", t(`Dependiente ${i + 1}: empleador`, `Dependent ${i + 1}: employer`));
        need(blank(d.annualIncome), pre + "annualIncome", t(`Dependiente ${i + 1}: ingreso`, `Dependent ${i + 1}: income`));
        need(blank(d.immigrationStatus), pre + "immigrationStatus", t(`Dependiente ${i + 1}: estatus migratorio`, `Dependent ${i + 1}: immigration status`));
        need(!d.sameAddress && blank(d.address), pre + "address", t(`Dependiente ${i + 1}: dirección`, `Dependent ${i + 1}: address`));
      });
    }
    // Custom required fields
    customFields.forEach((cf) => { if (cf.required) need(blank(form.customValues[cf.id]), `cf.${cf.id}`, cf.label); });
    // Las mismas, para cada persona del espejo. Solo se exigen a quien YA
    // empezó a llenarse: si no, un cónyuge en blanco bloquearía el envío.
    if (espejo) {
      const pedirA = (quien, etiqueta) => customFields.forEach((cf) => {
        if (!cf.required) return;
        need(blank(form.customValues[clave(quien, cf.id)]), `cf.${clave(quien, cf.id)}`, `${etiqueta}: ${cf.label}`);
      });
      if (sections.spouse && spouseStarted(form.spouse)) pedirA('spouse', t("Cónyuge", "Spouse"));
      if (sections.dependents) {
        form.dependents.forEach((d, i) => {
          if (depStarted(d)) pedirA(`dep.${i}`, t(`Dependiente ${i + 1}`, `Dependent ${i + 1}`));
        });
      }
    }
    return errs;
  }

  async function handleSubmit() {
    setSubmitError("");
    const errs = validate();
    if (errs.length) {
      setMissing(errs);
      setSubmitError(t("Faltan datos obligatorios (marcados en rojo): ", "Required fields are missing (marked in red): ") + errs.map((e) => e.label).join(" · "));
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!form.consent) { setSubmitError(t("Debes autorizar el uso de tu información.", "You must authorize the use of your information.")); return; }
    setMissing([]);
    setSubmitting(true);
    try {
      const primaryAddr = [form.address, form.city, form.state, form.zipCode].map((s) => s.trim()).filter(Boolean).join(", ");
      const personPayload = (p) => ({
        firstName: p.firstName.trim(), lastName: p.lastName.trim(),
        ...(p.relation !== undefined ? { relation: p.relation.trim() } : {}),
        email: p.email.trim(), phone: p.phone.trim(),
        dateOfBirth: p.dateOfBirth || undefined, ssn: p.ssn.trim(),
        employer: p.employer.trim(), annualIncome: p.annualIncome.trim(),
        immigrationStatus: p.immigrationStatus || undefined,
        sameAddress: !!p.sameAddress,
        address: p.sameAddress ? primaryAddr : p.address.trim(),
      });
      // Los dependientes vacios no se envian, asi que el que iba en la posicion
      // 2 puede llegar en la 1. Las respuestas del espejo van indexadas
      // (dep.0.*, dep.1.*), de modo que hay que RENUMERARLAS con la lista que
      // realmente se manda — si no, acaban pegadas a la persona equivocada.
      const depsQueSeEnvian = form.dependents
        .map((d, i) => ({ d, i }))
        .filter(({ d }) => depStarted(d));
      const customValuesFinal = {};
      Object.entries(form.customValues || {}).forEach(([k, v]) => {
        const m = /^dep\.(\d+)\.(.+)$/.exec(k);
        if (!m) { customValuesFinal[k] = v; return; }
        const nueva = depsQueSeEnvian.findIndex(({ i }) => i === Number(m[1]));
        if (nueva === -1) return;   // era de un dependiente que no se envia
        customValuesFinal[`dep.${nueva}.${m[2]}`] = v;
      });

      const payload = {
        firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim(), phone: form.phone.trim(),
        dateOfBirth: form.dateOfBirth || undefined, ssn: form.ssn.trim() || undefined,
        address: form.address.trim() || undefined, city: form.city.trim() || undefined, state: form.state.trim() || undefined, zipCode: form.zipCode.trim() || undefined,
        maritalStatus: form.maritalStatus || undefined, employer: form.employer.trim() || undefined, annualIncome: form.annualIncome.trim() || undefined, immigrationStatus: form.immigrationStatus || undefined,
        healthNotes: form.healthNotes.trim() || undefined,
        spouse: sections.spouse && spouseStarted(form.spouse) ? personPayload(form.spouse) : undefined,
        // Dependents store income under `income` (the ficha's dependent key), not `annualIncome`.
        dependents: sections.dependents
          ? depsQueSeEnvian.map(({ d }) => { const { annualIncome, ...rest } = personPayload(d); return { ...rest, income: annualIncome }; })
          : [],
        customValues: customValuesFinal,
        // EL AUTO. Solo van los bloques que la plantilla pidio, y de los
        // repetibles solo las filas que alguien empezo a llenar: un carro en
        // blanco no es un carro.
        auto: {
          ...(sections.autoDriver ? { conductor: form.auto.conductor } : {}),
          ...(sections.vehicles ? { carros: filasQueSeEnvian(form.auto.carros, CAMPOS_CARRO) } : {}),
          ...(sections.autoDrivers ? { conductores: filasQueSeEnvian(form.auto.conductores, CAMPOS_CONDUCTOR) } : {}),
          ...(sections.previousAddresses ? { direcciones: filasQueSeEnvian(form.auto.direcciones, CAMPOS_DIRECCION_ANTERIOR) } : {}),
          ...(sections.currentInsurance ? { seguroActual: form.auto.seguroActual } : {}),
          ...(sections.autoPolicy ? { poliza: form.auto.poliza } : {}),
          ...(sections.payment ? { pago: form.auto.pago } : {}),
        },
        consent: true,
      };
      const res = await submitPublicApplication(token, payload);
      setDone(res || {});
    } catch (err) {
      const msg = String(err.message || err);
      if (/already_submitted/.test(msg)) setDone({ already: true });
      else if (/invalid_dob/.test(msg)) setSubmitError(t("La fecha de nacimiento no es válida.", "The date of birth isn't valid."));
      else setSubmitError(msg);
    } finally { setSubmitting(false); }
  }

  // styles
  const page = { minHeight: "100vh", background: "linear-gradient(160deg,#eef6ff,#f8fbff)", padding: "24px 14px", boxSizing: "border-box", fontFamily: "system-ui,Arial,sans-serif", color: "#1a2433" };
  const card = { maxWidth: 600, margin: "0 auto", background: "#fff", borderRadius: 18, boxShadow: "0 10px 40px rgba(10,85,140,.12)", overflow: "hidden" };
  const pad = { padding: "22px 24px" };
  const inputSt = { width: "100%", padding: "10px 12px", borderRadius: 9, border: "1px solid #cbd5e1", fontSize: 15, boxSizing: "border-box" };
  const eb = (k) => (errSet.has(k) ? { borderColor: "#ef4444", background: "#fff5f5", boxShadow: "0 0 0 2px rgba(239,68,68,.12)" } : {});

  // Las mismas preguntas del titular, para UNA persona del espejo.
  //
  // Va como función normal y NO como componente: un componente declarado
  // dentro del render cambia de identidad en cada tecleo, React lo remonta y
  // el cursor se sale del campo a la primera letra.
  // Como se llama la persona del espejo. La logica vive en
  // `applicationMirror.js` para poder probarla: este archivo es `.jsx` y las
  // pruebas del backend no pueden importarlo.
  const nombreEspejo = (quien) => nombreDelEspejo(quien, form, t);

  const bloqueEspejo = (quien) => {
    if (!espejo) return null;
    if (!sections.health && customFields.length === 0) return null;
    return (
      <div style={{ borderTop: "1px dashed #cbd5e1", marginTop: 10, paddingTop: 10 }}>
        {/* CON EL NOMBRE DE LA PERSONA DELANTE (Lissette, 2026-08-26: *"que
            salga la pregunta del esposo y, bajo el nombre de la esposa, salga
            la misma pregunta; y si le da a dependientes, tambien bajo el
            nombre de cada dependiente"*).

            Antes eran las mismas preguntas repetidas detras de una linea de
            puntos, sin decir de quien eran. Quien llena el formulario ve la
            misma pregunta tres veces y no sabe si es un error de la pagina o
            si tiene que contestarla otra vez — y si contesta pensando en si
            mismo, la respuesta queda archivada bajo otra persona. En un
            formulario de seguros eso no es un detalle de presentacion: es un
            dato medico atribuido a quien no es. */}
        <div style={{ fontSize: 12.5, fontWeight: 800, color: "#0a558c", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
          <span aria-hidden="true">📝</span>
          <span>{t(`Las mismas preguntas, para ${nombreEspejo(quien)}`, `The same questions, for ${nombreEspejo(quien)}`)}</span>
        </div>
        {sections.health && (
          <Field label={t("Condiciones de salud / notas (opcional)", "Health conditions / notes (optional)")}>
            <textarea rows={2} style={{ ...inputSt, resize: "vertical", fontFamily: "inherit" }}
              value={form.customValues[clave(quien, HEALTH_KEY)] || ""}
              onChange={(e) => setCustom(clave(quien, HEALTH_KEY))(e.target.value)} />
          </Field>
        )}
        {customFields.map((cf) => (
          <Field key={cf.id} label={`${cf.label}${cf.required ? " *" : ""}`}>
            <CustomFieldInput field={cf}
              value={form.customValues[clave(quien, cf.id)] || ""}
              onChange={setCustom(clave(quien, cf.id))}
              inputSt={{ ...inputSt, ...eb(`cf.${clave(quien, cf.id)}`) }} t={t} />
            {cf.helpText && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>{cf.helpText}</div>}
          </Field>
        ))}
      </div>
    );
  };

  // Un enlace VENCIDO y uno roto no se cuentan igual. El vencido tiene arreglo
  // —el agente pulsa Reenviar y llega otro— y decirlo es lo que convierte una
  // llamada enfadada en un correo nuevo. El enlace dura 72 horas a propósito:
  // pide el seguro social y no puede quedarse vivo tres meses en un buzón.
  if (loadError) return (
    <Shell page={page} card={card} pad={pad}>
      <AlertCircle size={34} style={{ color: linkGone ? "#d97706" : "#e11d48", marginBottom: 8 }} />
      <h2 style={{ margin: "6px 0" }}>
        {linkGone ? t("Este enlace ya venció", "This link has expired") : t("Enlace no disponible", "Link unavailable")}
      </h2>
      <p style={{ color: "#64748b", fontSize: 14 }}>
        {linkGone
          ? t("Por tu seguridad, el formulario deja de abrirse a las 72 horas. Pídele a tu agente que te reenvíe el enlace: te llega uno nuevo al instante y no tendrás que empezar de cero.",
              "For your security, the form stops opening after 72 hours. Ask your agent to resend the link — a new one arrives right away and you won't have to start over.")
          : t("Este enlace no existe o ya expiró. Verifica con tu agente.",
              "This link doesn't exist or has expired. Check with your agent.")}
      </p>
    </Shell>
  );
  if (!data) return <Shell page={page} card={card} pad={pad} c="#64748b"><Loader2 size={20} className="dkt-spin" /> {t("Cargando…", "Loading…")}</Shell>;
  if (data.alreadyDone || (done && done.already)) return <Shell page={page} card={card} pad={pad}><CheckCircle2 size={40} style={{ color: "#16a34a", marginBottom: 8 }} /><h2 style={{ margin: "6px 0" }}>{t("Tu aplicación ya está completa", "Your application is already complete")}</h2><p style={{ color: "#64748b", fontSize: 14 }}>{t("Ya recibimos tus datos. Si necesitas cambiar algo, contacta a tu agente.", "We already have your details. To change anything, contact your agent.")}</p></Shell>;
  if (done) return <Shell page={page} card={card} pad={pad}><div style={{ width: 64, height: 64, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><Check size={34} style={{ color: "#16a34a" }} /></div><h2 style={{ margin: "4px 0" }}>{t("¡Aplicación enviada! 🎉", "Application sent! 🎉")}</h2><p style={{ color: "#475569", fontSize: 14, marginTop: 10 }}>{t(`Gracias. ${done.agentName || "Tu agente"} ya tiene tu información.`, `Thank you. ${done.agentName || "Your agent"} now has your information.`)}</p></Shell>;

  const agencyName = data.agencyName || "";
  const req = " *";

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ ...pad, background: "linear-gradient(135deg,#0a558c,#0891b2)", color: "#fff" }}>
          {data.agencyLogoUrl && <div style={{ textAlign: "center", marginBottom: 12 }}><img src={data.agencyLogoUrl} alt={agencyName || ""} style={{ maxHeight: 56, maxWidth: 210, objectFit: "contain", background: "#fff", borderRadius: 10, padding: "7px 12px", boxSizing: "border-box" }} /></div>}
          <div style={{ fontWeight: 800, fontSize: 19 }}>{data.title || t("Completa tu aplicación", "Complete your application")}</div>
          {agencyName && <div style={{ fontSize: 13, opacity: .9, marginTop: 2 }}>{agencyName}</div>}
          {data.plan && <div style={{ marginTop: 12, background: "rgba(255,255,255,.15)", borderRadius: 10, padding: "10px 14px", fontSize: 13.5 }}>✅ {t("Plan elegido", "Chosen plan")}: <strong>{[data.plan.carrier, data.plan.description].filter(Boolean).join(" · ")}</strong>{data.plan.price ? ` · $${Number(data.plan.price).toFixed(2)}/mo` : ""}</div>}
        </div>

        <div style={pad}>
          {data.intro && <p style={{ color: "#475569", fontSize: 14, marginTop: 0 }}>{data.intro}</p>}
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10, padding: "9px 12px", marginBottom: 4, fontSize: 12.5, color: "#0a558c" }}><ShieldCheck size={16} /> {t("Tus datos viajan y se guardan cifrados.", "Your information is transmitted and stored encrypted.")}</div>

          {submitError && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", borderRadius: 9, padding: "11px 13px", fontSize: 13.5, margin: "12px 0", fontWeight: 500 }}>{submitError}</div>}

          {/* Contact (always) */}
          <Section title={t("Datos de contacto", "Contact details")}>
            <Row>
              <Field label={t("Nombre", "First name") + req}><input style={{ ...inputSt, ...eb("firstName") }} value={form.firstName} onChange={set("firstName")} /></Field>
              <Field label={t("Apellido", "Last name") + req}><input style={{ ...inputSt, ...eb("lastName") }} value={form.lastName} onChange={set("lastName")} /></Field>
            </Row>
            <Field label={t("Correo electrónico", "Email") + req}><input type="email" style={{ ...inputSt, ...eb("email") }} value={form.email} onChange={set("email")} /></Field>
            <Field label={t("Teléfono", "Phone") + req}><input type="tel" style={{ ...inputSt, ...eb("phone") }} value={form.phone} onChange={set("phone")} /></Field>
          </Section>

          {/* La fecha de nacimiento y el SSN son DOS casillas de la plantilla,
              no una (Lissette 2026-09-04, punto 67): se puede pedir la fecha
              sin pedir el numero de seguro social, que es el dato mas delicado
              del formulario. La seccion entera desaparece si no se pide
              ninguno de los dos. */}
          {(sections.dob || sections.ssn) && (
            <Section title={t("Identificación", "Identification")}>
              <Row>
                {sections.dob && <Field label={t("Fecha de nacimiento", "Date of birth") + req}><input type="date" style={{ ...inputSt, ...eb("dateOfBirth") }} value={form.dateOfBirth} onChange={set("dateOfBirth")} /></Field>}
                {sections.ssn && <Field label={t("Seguro Social (SSN/ITIN)", "Social Security (SSN/ITIN)") + req}><input style={{ ...inputSt, ...eb("ssn") }} value={form.ssn} onChange={set("ssn")} placeholder="•••-••-••••" inputMode="numeric" /></Field>}
              </Row>
            </Section>
          )}

          {sections.address && (
            <Section title={t("Dirección", "Address")}>
              <Field label={t("Dirección", "Street address") + req}><input style={{ ...inputSt, ...eb("address") }} value={form.address} onChange={set("address")} /></Field>
              <Row>
                <Field label={t("Ciudad", "City") + req}><input style={{ ...inputSt, ...eb("city") }} value={form.city} onChange={set("city")} /></Field>
                <Field label={t("Estado", "State") + req}><input style={{ ...inputSt, ...eb("state") }} value={form.state} onChange={set("state")} maxLength={20} /></Field>
                <Field label={t("Código postal", "ZIP") + req}><input style={{ ...inputSt, ...eb("zipCode") }} value={form.zipCode} onChange={set("zipCode")} maxLength={12} /></Field>
              </Row>
              {/* Tipo de vivienda, dueno o alquilado y tiempo en la direccion
                  (Lissette 2026-09-09). Se preguntan AQUI, con la direccion,
                  pero el dato sigue viviendo en el bloque del conductor: una
                  aplicacion enviada antes de este cambio se lee igual. */}
              {sections.autoDriver && viviendaVaConLaDireccion(sections) && (
                <BloqueAuto campos={CAMPOS_CON_LA_DIRECCION} bloque="conductor" datos={form.auto.conductor} setAuto={setAuto} inputSt={inputSt} lang={EN ? 'en' : 'es'} t={t} />
              )}
            </Section>
          )}

          {sections.employment && (
            <Section title={t("Empleo y estado", "Employment & status")}>
              <Row>
                <Field label={t("Estado civil", "Marital status") + req}><select style={{ ...inputSt, ...eb("maritalStatus") }} value={form.maritalStatus} onChange={set("maritalStatus")}>{MARITAL(EN).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                <Field label={t("Estatus migratorio", "Immigration status") + req}><select style={{ ...inputSt, ...eb("immigrationStatus") }} value={form.immigrationStatus} onChange={set("immigrationStatus")}>{IMMIGRATION(EN).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
              </Row>
              <Row>
                <Field label={t("Empleador", "Employer") + req}><input style={{ ...inputSt, ...eb("employer") }} value={form.employer} onChange={set("employer")} /></Field>
                <Field label={t("Ingreso anual", "Annual income") + req}><input style={{ ...inputSt, ...eb("annualIncome") }} value={form.annualIncome} onChange={set("annualIncome")} /></Field>
              </Row>
            </Section>
          )}

          {/* ── EL FORMULARIO DE AUTO (Lissette 2026-09-04, punto 69) ──────────
              Los seis bloques de su hoja de calculo, en su orden y con sus
              mismas preguntas y desplegables. Los tres repetibles —carros,
              conductores adicionales y direcciones anteriores— no tienen tope:
              su hoja se quedaba en cuatro carros y tres conductores, y eso es
              justo lo que pidio cambiar. */}

          {sections.autoDriver && (
            <Section title={t("Datos del conductor", "Driver information")}>
              <BloqueAuto campos={viviendaVaConLaDireccion(sections) ? CAMPOS_CONDUCTOR_TITULAR : [...CAMPOS_CONDUCTOR_TITULAR, ...CAMPOS_CON_LA_DIRECCION]} bloque="conductor" datos={form.auto.conductor} setAuto={setAuto} inputSt={inputSt} lang={EN ? 'en' : 'es'} t={t} />
            </Section>
          )}

          {sections.vehicles && (
            <Repetidor
              bloque="carros" campos={CAMPOS_CARRO}
              filas={form.auto.carros} setFila={setFila} addFila={addFila} delFila={delFila}
              inputSt={inputSt} lang={EN ? 'en' : 'es'} t={t}
              titulo={t("Carros", "Cars")} rotulo={t("Carro", "Car")}
              agregar={t("Agregar otro carro", "Add another car")}
            />
          )}

          {sections.previousAddresses && (
            <Repetidor
              bloque="direcciones" campos={CAMPOS_DIRECCION_ANTERIOR}
              filas={form.auto.direcciones} setFila={setFila} addFila={addFila} delFila={delFila}
              inputSt={inputSt} lang={EN ? 'en' : 'es'} t={t}
              titulo={t("Direcciones anteriores", "Previous addresses")} rotulo={t("Dirección", "Address")}
              agregar={t("Agregar otra dirección", "Add another address")}
            />
          )}

          {sections.currentInsurance && (
            <Section title={t("Seguro actual", "Current insurance")}>
              <BloqueAuto campos={CAMPOS_SEGURO_ACTUAL} bloque="seguroActual" datos={form.auto.seguroActual} setAuto={setAuto} inputSt={inputSt} lang={EN ? 'en' : 'es'} t={t} />
            </Section>
          )}

          {sections.autoPolicy && (
            <Section title={t("Preferencias de la póliza", "Policy preferences")}>
              <BloqueAuto campos={CAMPOS_POLIZA_AUTO} bloque="poliza" datos={form.auto.poliza} setAuto={setAuto} inputSt={inputSt} lang={EN ? 'en' : 'es'} t={t} />
            </Section>
          )}

          {sections.autoDrivers && (
            <Repetidor
              bloque="conductores" campos={CAMPOS_CONDUCTOR}
              filas={form.auto.conductores} setFila={setFila} addFila={addFila} delFila={delFila}
              inputSt={inputSt} lang={EN ? 'en' : 'es'} t={t}
              titulo={t("Conductores adicionales", "Additional drivers")} rotulo={t("Conductor", "Driver")}
              agregar={t("Agregar otro conductor", "Add another driver")}
            />
          )}

          {/* Salud y las preguntas adicionales del TITULAR van AQUI, pegadas a
              sus datos y ANTES del conyuge — no al final del formulario.

              Estaban abajo del todo, detras de Conyuge y Dependientes. Como a
              esas dos personas sus preguntas les salen dentro de su propio
              bloque, el titular parecia el unico que no las tenia: se llegaba
              a "Conyuge" sin haber visto ni una pregunta suya
              (Lissette, 2026-08-26: *"las preguntas adicionales no le salen
              debajo de la informacion del principal, solo conyuge y
              dependiente"*).

              Ahora el formulario se lee por PERSONAS: todo lo del titular
              junto, y despues cada acompanante con lo suyo. */}
          {sections.health && (
            <Section title={t("Salud", "Health")}>
              <Field label={t("Condiciones de salud / notas (opcional)", "Health conditions / notes (optional)")}><textarea rows={3} style={{ ...inputSt, resize: "vertical", fontFamily: "inherit" }} value={form.healthNotes} onChange={set("healthNotes")} /></Field>
            </Section>
          )}

          {customFields.length > 0 && (
            <Section title={t("Información adicional", "Additional information")}>
              {customFields.map((cf) => (
                <Field key={cf.id} label={`${cf.label}${cf.required ? " *" : ""}`}>
                  <CustomFieldInput field={cf} value={form.customValues[cf.id] || ""} onChange={setCustom(cf.id)} inputSt={{ ...inputSt, ...eb(`cf.${cf.id}`) }} t={t} />
                  {cf.helpText && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>{cf.helpText}</div>}
                </Field>
              ))}
            </Section>
          )}

          {sections.spouse && (
            <Section title={t("Cónyuge", "Spouse")}>
              <Row>
                <Field label={t("Nombre", "First name")}><input style={{ ...inputSt, ...eb("spouse.firstName") }} value={form.spouse.firstName} onChange={setSpouse("firstName")} /></Field>
                <Field label={t("Apellido", "Last name")}><input style={{ ...inputSt, ...eb("spouse.lastName") }} value={form.spouse.lastName} onChange={setSpouse("lastName")} /></Field>
              </Row>
              <Row>
                <Field label={t("Correo", "Email")}><input type="email" style={{ ...inputSt, ...eb("spouse.email") }} value={form.spouse.email} onChange={setSpouse("email")} /></Field>
                <Field label={t("Teléfono", "Phone")}><input type="tel" style={{ ...inputSt, ...eb("spouse.phone") }} value={form.spouse.phone} onChange={setSpouse("phone")} /></Field>
              </Row>
              {/* Al conyuge se le pide lo MISMO que al titular. Antes la fecha
                  y el SSN salian siempre, asi que apagar la casilla arriba no
                  servia de nada: el formulario seguia pidiendo el numero, solo
                  que a otra persona (Lissette 2026-09-04, punto 68). La fila
                  entera se va si no se pide ninguno de los dos: si no, quedaria
                  un hueco vacio en medio del bloque. */}
              {(sections.dob || sections.ssn) && <Row>
                {sections.dob && <Field label={t("Fecha de nacimiento", "Date of birth")}><input type="date" style={{ ...inputSt, ...eb("spouse.dateOfBirth") }} value={form.spouse.dateOfBirth} onChange={setSpouse("dateOfBirth")} /></Field>}
                {sections.ssn && <Field label="SSN/ITIN"><input style={{ ...inputSt, ...eb("spouse.ssn") }} value={form.spouse.ssn} onChange={setSpouse("ssn")} inputMode="numeric" /></Field>}
              </Row>}
              <Row>
                <Field label={t("Empleador", "Employer")}><input style={{ ...inputSt, ...eb("spouse.employer") }} value={form.spouse.employer} onChange={setSpouse("employer")} /></Field>
                <Field label={t("Ingreso anual", "Annual income")}><input style={{ ...inputSt, ...eb("spouse.annualIncome") }} value={form.spouse.annualIncome} onChange={setSpouse("annualIncome")} /></Field>
              </Row>
              <Field label={t("Estatus migratorio", "Immigration status")}><select style={{ ...inputSt, ...eb("spouse.immigrationStatus") }} value={form.spouse.immigrationStatus} onChange={setSpouse("immigrationStatus")}>{IMMIGRATION(EN).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
              <SameAddress checked={form.spouse.sameAddress} onToggle={setSpouse("sameAddress")} addrValue={form.spouse.address} onAddr={setSpouse("address")} err={eb("spouse.address")} inputSt={inputSt} t={t} />
              {bloqueEspejo("spouse")}
            </Section>
          )}

          {sections.dependents && (
            <Section title={t("Dependientes", "Dependents")}>
              {form.dependents.map((d, i) => (
                <div key={i} style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: "12px 14px", marginBottom: 10, position: "relative" }}>
                  <button type="button" onClick={() => delDep(i)} title={t("Quitar", "Remove")} style={{ position: "absolute", top: 8, right: 8, background: "none", border: "none", cursor: "pointer", color: "#ef4444" }}><Trash2 size={16} /></button>
                  <Row>
                    <Field label={t("Nombre", "First name")}><input style={{ ...inputSt, ...eb(`dep.${i}.firstName`) }} value={d.firstName} onChange={setDep(i, "firstName")} /></Field>
                    <Field label={t("Apellido", "Last name")}><input style={{ ...inputSt, ...eb(`dep.${i}.lastName`) }} value={d.lastName} onChange={setDep(i, "lastName")} /></Field>
                  </Row>
                  <Row>
                    <Field label={t("Parentesco", "Relationship")}><input style={{ ...inputSt, ...eb(`dep.${i}.relation`) }} value={d.relation} onChange={setDep(i, "relation")} placeholder={t("hijo/a, etc.", "child, etc.")} /></Field>
                    {sections.dob && <Field label={t("Fecha de nacimiento", "Date of birth")}><input type="date" style={{ ...inputSt, ...eb(`dep.${i}.dateOfBirth`) }} value={d.dateOfBirth} onChange={setDep(i, "dateOfBirth")} /></Field>}
                  </Row>
                  <Row>
                    <Field label={t("Correo", "Email")}><input type="email" style={{ ...inputSt, ...eb(`dep.${i}.email`) }} value={d.email} onChange={setDep(i, "email")} /></Field>
                    <Field label={t("Teléfono", "Phone")}><input type="tel" style={{ ...inputSt, ...eb(`dep.${i}.phone`) }} value={d.phone} onChange={setDep(i, "phone")} /></Field>
                  </Row>
                  <Row>
                    {sections.ssn && <Field label="SSN/ITIN"><input style={{ ...inputSt, ...eb(`dep.${i}.ssn`) }} value={d.ssn} onChange={setDep(i, "ssn")} inputMode="numeric" /></Field>}
                    <Field label={t("Estatus migratorio", "Immigration status")}><select style={{ ...inputSt, ...eb(`dep.${i}.immigrationStatus`) }} value={d.immigrationStatus} onChange={setDep(i, "immigrationStatus")}>{IMMIGRATION(EN).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                  </Row>
                  <Row>
                    <Field label={t("Empleador", "Employer")}><input style={{ ...inputSt, ...eb(`dep.${i}.employer`) }} value={d.employer} onChange={setDep(i, "employer")} /></Field>
                    <Field label={t("Ingreso anual", "Annual income")}><input style={{ ...inputSt, ...eb(`dep.${i}.annualIncome`) }} value={d.annualIncome} onChange={setDep(i, "annualIncome")} /></Field>
                  </Row>
                  <SameAddress checked={d.sameAddress} onToggle={setDep(i, "sameAddress")} addrValue={d.address} onAddr={setDep(i, "address")} err={eb(`dep.${i}.address`)} inputSt={inputSt} t={t} />
                  {bloqueEspejo(`dep.${i}`)}
                </div>
              ))}
              <button type="button" onClick={addDep} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#ecfeff", border: "1px solid #bae6fd", color: "#0a558c", borderRadius: 9, padding: "8px 14px", fontWeight: 700, fontSize: 13.5, cursor: "pointer" }}><Plus size={15} /> {t("Agregar dependiente", "Add dependent")}</button>
            </Section>
          )}



          {/* EL PAGO va al final, como en su hoja. Dos cosas a proposito:
              - El CVC no se pide. Guardarlo esta prohibido por las reglas de
                las tarjetas (PCI-DSS 3.2) aunque se cifre, y esto entra por un
                enlace publico y sin sesion.
              - Los numeros no se quedan en la solicitud: van al metodo de pago
                del cliente, cifrados y detras del PIN de siempre. */}
          {sections.payment && (
            <Section title={t("Información de pago", "Payment information")}>
              <BloqueAuto campos={CAMPOS_PAGO} bloque="pago" datos={form.auto.pago} setAuto={setAuto} inputSt={inputSt} lang={EN ? 'en' : 'es'} t={t} />
              <div style={{ fontSize: 12, color: "#64748b", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 9, padding: "9px 11px", marginTop: 2, lineHeight: 1.45 }}>
                🔒 {t("Estos números se guardan cifrados y solo tu agente puede verlos, con su clave. No te pedimos el código de seguridad de la tarjeta (CVC).",
                      "These numbers are stored encrypted and only your agent can see them, with their PIN. We don't ask for your card security code (CVC).")}
              </div>
            </Section>
          )}

          <label style={{ display: "flex", gap: 9, alignItems: "flex-start", margin: "18px 0 16px", cursor: "pointer", fontSize: 13, color: "#475569" }}>
            <input type="checkbox" checked={form.consent} onChange={set("consent")} style={{ marginTop: 3, width: 16, height: 16, flex: "0 0 auto" }} />
            <span>{t(`Autorizo a ${agencyName || "mi agente"} a usar esta información para procesar mi solicitud de seguro.`, `I authorize ${agencyName || "my agent"} to use this information to process my insurance application.`)}</span>
          </label>

          <button type="button" onClick={handleSubmit} disabled={submitting} style={{ width: "100%", padding: "13px", borderRadius: 11, border: "none", background: "linear-gradient(135deg,#0a558c,#0891b2)", color: "#fff", fontSize: 15.5, fontWeight: 800, cursor: submitting ? "wait" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {submitting ? <Loader2 size={18} className="dkt-spin" /> : <Check size={18} />} {t("Enviar mi aplicación", "Submit my application")}
          </button>
          <div style={{ fontSize: 11.5, color: "#94a3b8", textAlign: "center", marginTop: 8 }}>{t("Los campos con * son obligatorios.", "Fields marked * are required.")}</div>
        </div>
        <div style={{ textAlign: "center", padding: "12px", fontSize: 11.5, color: "#94a3b8" }}>{t("Protegido con", "Secured with")} <strong style={{ color: "#0891b2" }}>Deskte</strong></div>
      </div>
    </div>
  );
}

function SameAddress({ checked, onToggle, addrValue, onAddr, err, inputSt, t }) {
  return (
    <div style={{ marginTop: 4 }}>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, color: "#475569", cursor: "pointer", marginBottom: checked ? 0 : 8 }}>
        <input type="checkbox" checked={checked} onChange={onToggle} style={{ width: 15, height: 15 }} /> {t("Mismo domicilio que el titular", "Same address as the applicant")}
      </label>
      {!checked && (
        <input style={{ ...inputSt, ...err }} value={addrValue} onChange={onAddr} placeholder={t("Dirección completa", "Full address")} />
      )}
    </div>
  );
}

function CustomFieldInput({ field, value, onChange, inputSt, t }) {
  const tr = t || ((es) => es);
  const set = (e) => onChange(e.target.value);
  if (field.type === "textarea") return <textarea rows={3} style={{ ...inputSt, resize: "vertical", fontFamily: "inherit" }} value={value} onChange={set} />;
  if (field.type === "select") return <select style={inputSt} value={value} onChange={set}><option value="">—</option>{(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>;
  if (field.type === "radio") return <div>{(field.options || []).map((o) => <label key={o} style={{ display: "flex", gap: 6, alignItems: "center", margin: "3px 0", fontSize: 14 }}><input type="radio" name={field.id} checked={value === o} onChange={() => onChange(o)} /> {o}</label>)}</div>;
  // Sí/No: two SQUARE boxes, mutually exclusive (both read the same value, so
  // checking one clears the other). Clicking the checked box again clears it.
  if (field.type === "yesno") {
    const opts = [tr("Sí", "Yes"), tr("No", "No")];
    return <div style={{ display: "flex", gap: 20 }}>{opts.map((o) => (
      <label key={o} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14, cursor: "pointer" }}>
        <input type="checkbox" checked={value === o} onChange={() => onChange(value === o ? "" : o)} /> {o}
      </label>
    ))}</div>;
  }
  if (field.type === "checkbox") return <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14 }}><input type="checkbox" checked={value === "yes"} onChange={(e) => onChange(e.target.checked ? "yes" : "")} /> {field.label}</label>;
  const htmlType = field.type === "phone" ? "tel" : (["email", "number", "date"].includes(field.type) ? field.type : "text");
  return <input type={htmlType} style={inputSt} value={value} onChange={set} />;
}

function Shell({ page, card, pad, c, children }) {
  return <div style={page}><div style={{ ...card, ...pad, textAlign: "center", ...(c ? { color: c } : {}) }}>{children}</div></div>;
}
// ── EL FORMULARIO DE AUTO, EN EL AMBITO DEL MODULO ──────────────────────────
//
// Estos tres estaban declarados DENTRO de ApplicationPublicPage y por eso el
// cliente solo podia escribir una letra: cada tecla llama a `setForm`, eso
// vuelve a dibujar la pantalla, y al volver a dibujarla `const CampoAuto = ...`
// creaba una FUNCION NUEVA. Para React una funcion distinta es un componente
// distinto, asi que desmontaba el <input> y montaba otro en su sitio; el que
// tenia el foco ya no existia y el foco caia al <body>. Se escribe la letra,
// se pierde el campo, hay que volver a hacer clic. Cada vez.
//
// Es EL MISMO fallo del 2026-07-15 con `Section`, cuyo aviso esta veinte lineas
// mas abajo — y aun asi volvio a entrar por la puerta de al lado al añadir el
// punto 69. La regla no es "acuerdate de Section": es que NINGUN componente se
// declara dentro de otro. Si necesita datos de la pantalla, se le pasan por
// props, que es lo que se hace aqui.
//
// Todo lo que antes tomaban del cierre —`inputSt`, `EN`, `t`, `form.auto` y los
// manejadores— entra ahora como prop.

/** Los campos de un bloque, de dos en dos, como en su hoja de calculo. */
function parejas(campos) {
  return (campos || []).reduce((acc, c, i) => {
    if (i % 2 === 0) acc.push([c]); else acc[acc.length - 1].push(c);
    return acc;
  }, []);
}

/** Un campo del catalogo. `tipo` decide el control: cuatro tipos es todo lo que
 *  hay en su hoja. */
function CampoAuto({ def, valor, onChange, inputSt, lang, t }) {
  return (
    <Field label={etiqueta(def, lang)}>
      {def.tipo === 'lista' ? (
        <select style={inputSt} value={valor || ''} onChange={onChange}>
          <option value="">{t('\u2014 elige \u2014', '\u2014 choose \u2014')}</option>
          {(def.opciones || []).map((o) => <option key={o.v} value={o.v}>{etiqueta(o, lang)}</option>)}
        </select>
      ) : (
        <input
          type={def.tipo === 'fecha' ? 'date' : def.tipo === 'numero' ? 'number' : 'text'}
          inputMode={def.tipo === 'numero' ? 'numeric' : undefined}
          style={inputSt} value={valor || ''} onChange={onChange}
        />
      )}
    </Field>
  );
}

/** Un bloque de una sola ficha: conductor titular, seguro actual, poliza, pago. */
function BloqueAuto({ campos, bloque, datos, setAuto, inputSt, lang, t }) {
  return (
    <>{parejas(campos).map((par, i) => (
      <Row key={i}>{par.map((def) => (
        <CampoAuto key={def.key} def={def} valor={(datos || {})[def.key]}
          onChange={setAuto(bloque, def.key)} inputSt={inputSt} lang={lang} t={t} />
      ))}</Row>
    ))}</>
  );
}

/** Un bloque repetible: carros, conductores adicionales, direcciones anteriores.
 *  Es la mitad de lo que ella pidio —"poder agregar mas dependiente y mas
 *  carro"—, asi que el boton de agregar no tiene tope. */
function Repetidor({ bloque, campos, titulo, rotulo, agregar, filas, setFila, addFila, delFila, inputSt, lang, t }) {
  return (
    <Section title={titulo}>
      {(filas || []).map((fila, i) => (
        <div key={i} style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: "12px 14px", marginBottom: 10, position: "relative" }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#0a558c", marginBottom: 8 }}>{rotulo} {i + 1}</div>
          <button type="button" onClick={delFila(bloque, i)} title={t("Quitar", "Remove")}
            style={{ position: "absolute", top: 8, right: 8, background: "none", border: "none", cursor: "pointer", color: "#ef4444" }}>
            <Trash2 size={16} />
          </button>
          {parejas(campos).map((par, j) => (
            <Row key={j}>{par.map((def) => (
              <CampoAuto key={def.key} def={def} valor={fila[def.key]}
                onChange={setFila(bloque, i, def.key)} inputSt={inputSt} lang={lang} t={t} />
            ))}</Row>
          ))}
        </div>
      ))}
      <button type="button" onClick={addFila(bloque, campos)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#ecfeff", border: "1px solid #bae6fd", color: "#0a558c", borderRadius: 9, padding: "8px 14px", fontWeight: 700, fontSize: 13.5, cursor: "pointer" }}>
        <Plus size={15} /> {agregar}
      </button>
    </Section>
  );
}

// Section MUST live at module scope. Defining it inside ApplicationPublicPage
// created a new component identity on every render, so each keystroke (which
// re-renders via setForm) remounted the whole section — destroying the focused
// input and dropping focus to <body>. That's why recipients could only type one
// letter before having to click again. (bug reported 2026-07-15)
function Section({ title, children }) {
  return <div style={{ marginTop: 18 }}><div style={{ fontSize: 13, fontWeight: 800, color: "#0a558c", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>{title}</div>{children}</div>;
}
function Field({ label, children }) {
  // pre-wrap: una pregunta larga con saltos de línea se lee como la escribió el agente (E-407).
  return <div style={{ marginBottom: 12, flex: 1, minWidth: 0 }}><label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#475569", marginBottom: 5, whiteSpace: "pre-wrap" }}>{label}</label>{children}</div>;
}
function Row({ children }) { return <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>{children}</div>; }
function spouseStarted(s) { return ["firstName", "lastName", "email", "phone", "dateOfBirth", "ssn", "employer", "annualIncome", "immigrationStatus"].some((k) => String(s[k] || "").trim()); }
function depStarted(d) { return ["firstName", "lastName", "relation", "email", "phone", "dateOfBirth", "ssn", "employer", "annualIncome", "immigrationStatus"].some((k) => String(d[k] || "").trim()); }
function emptyDep() { return { firstName: "", lastName: "", relation: "", email: "", phone: "", dateOfBirth: "", ssn: "", employer: "", annualIncome: "", immigrationStatus: "", sameAddress: true, address: "" }; }
function emptyForm() {
  return {
    firstName: "", lastName: "", email: "", phone: "", dateOfBirth: "", ssn: "",
    address: "", city: "", state: "", zipCode: "", maritalStatus: "", employer: "", annualIncome: "", immigrationStatus: "", healthNotes: "",
    spouse: { firstName: "", lastName: "", email: "", phone: "", dateOfBirth: "", ssn: "", employer: "", annualIncome: "", immigrationStatus: "", sameAddress: true, address: "" },
    dependents: [], customValues: {}, consent: false,
    // EL FORMULARIO DE AUTO (punto 69). Todo colgando de `auto` para que el
    // resto del formulario no cambie ni una linea: si la plantilla no pide
    // auto, esto se queda en blanco y no viaja.
    auto: {
      conductor: filaVacia([...CAMPOS_CONDUCTOR_TITULAR, ...CAMPOS_CON_LA_DIRECCION]),
      carros: [filaVacia(CAMPOS_CARRO)],
      conductores: [],
      direcciones: [],
      seguroActual: filaVacia(CAMPOS_SEGURO_ACTUAL),
      poliza: filaVacia(CAMPOS_POLIZA_AUTO),
      pago: filaVacia(CAMPOS_PAGO),
    },
  };
}
